/**
 * Staff Clock In/Out Page
 * ========================
 * 
 * PIN-based time tracking for staff members.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Clock,
  LogIn,
  LogOut,
  User,
  ArrowLeft,
  Loader2,
  Check,
  X,
  KeyRound,
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const POSClockPage = () => {
  const { token } = useAuth();
  const [pin, setPin] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [staff, setStaff] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [allStaffStatus, setAllStaffStatus] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch all staff status on mount
  useEffect(() => {
    fetchStaffStatus();
  }, []);

  const fetchStaffStatus = async () => {
    try {
      const res = await fetch(`${API}/pos/clock/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllStaffStatus(data);
      }
    } catch (err) {
      console.error("Error fetching status:", err);
    }
  };

  const handlePinInput = (digit) => {
    if (pin.length < 6) {
      setPin(pin + digit);
    }
  };

  const handleClear = () => {
    setPin("");
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  const verifyPin = async () => {
    if (pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    
    setVerifying(true);
    try {
      const res = await fetch(`${API}/pos/clock/verify-pin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ pin })
      });
      
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
        setPin("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Invalid PIN");
        setPin("");
      }
    } catch (err) {
      console.error("Error verifying PIN:", err);
      toast.error("Failed to verify PIN");
      setPin("");
    } finally {
      setVerifying(false);
    }
  };

  const handleClockAction = async (action) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/pos/clock/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ staff_id: staff.staff_id, action })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setStaff(null);
        fetchStaffStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || `Failed to clock ${action}`);
      }
    } catch (err) {
      console.error(`Error clocking ${action}:`, err);
      toast.error(`Failed to clock ${action}`);
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/merchant/pos/terminal" className="flex items-center gap-2">
            <ArrowLeft className="h-5 w-5 text-white" />
            <img src={COMPANY_LOGO} alt="Veyronix" className="h-8 brightness-0 invert" />
          </Link>
          <div className="text-right">
            <p className="text-3xl font-bold font-mono text-white">
              {currentTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </p>
            <p className="text-sm text-white/80">
              {currentTime.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left: PIN Entry / Clock Action */}
          <div className="space-y-6">
            {!staff ? (
              /* PIN Entry */
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="h-8 w-8 text-indigo-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Enter Your PIN</h2>
                  <p className="text-gray-500 text-sm mt-1">Clock in or out with your staff PIN</p>
                </div>

                {/* PIN Display */}
                <div className="flex justify-center gap-2 mb-6">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${
                        pin.length > i
                          ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      {pin.length > i ? "•" : ""}
                    </div>
                  ))}
                </div>

                {/* Keypad */}
                <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                    <button
                      key={digit}
                      onClick={() => handlePinInput(digit.toString())}
                      className="h-14 rounded-xl bg-gray-100 hover:bg-indigo-100 text-gray-900 text-xl font-bold transition-colors"
                    >
                      {digit}
                    </button>
                  ))}
                  <button
                    onClick={handleClear}
                    className="h-14 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => handlePinInput("0")}
                    className="h-14 rounded-xl bg-gray-100 hover:bg-indigo-100 text-gray-900 text-xl font-bold transition-colors"
                  >
                    0
                  </button>
                  <button
                    onClick={handleBackspace}
                    className="h-14 rounded-xl bg-gray-100 hover:bg-indigo-100 text-gray-900 text-xl font-bold transition-colors"
                  >
                    ←
                  </button>
                </div>

                <Button
                  onClick={verifyPin}
                  disabled={pin.length < 4 || verifying}
                  className="w-full mt-6 h-14 bg-indigo-600 hover:bg-indigo-700 text-white text-lg"
                  data-testid="verify-pin-btn"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Confirm
                    </>
                  )}
                </Button>
              </div>
            ) : (
              /* Clock Action */
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <User className="h-10 w-10 text-indigo-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{staff.name}</h2>
                  <p className="text-gray-500 capitalize">{staff.role}</p>
                </div>

                {staff.is_clocked_in ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                      <p className="text-emerald-400 font-medium">Currently Clocked In</p>
                      <p className="text-2xl font-bold mt-2">Since {formatTime(staff.clock_in_time)}</p>
                    </div>
                    <Button
                      onClick={() => handleClockAction("out")}
                      disabled={processing}
                      className="w-full h-16 bg-red-600 hover:bg-red-700 text-xl"
                      data-testid="clock-out-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <>
                          <LogOut className="h-6 w-6 mr-3" />
                          Clock Out
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleClockAction("in")}
                    disabled={processing}
                    className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-xl"
                    data-testid="clock-in-btn"
                  >
                    {processing ? (
                      <Loader2 className="h-6 w-6 animate-spin" />
                    ) : (
                      <>
                        <LogIn className="h-6 w-6 mr-3" />
                        Clock In
                      </>
                    )}
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => setStaff(null)}
                  className="w-full mt-4 border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Right: Staff Status */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-900">
              <Clock className="h-5 w-5 text-indigo-600" />
              Staff Status Today
            </h2>
            
            {allStaffStatus.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No staff members</p>
            ) : (
              <div className="space-y-3">
                {allStaffStatus.map((s) => (
                  <div
                    key={s.staff_id}
                    className={`p-4 rounded-xl border ${
                      s.is_clocked_in
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{s.name}</p>
                        <p className="text-sm text-gray-500 capitalize">{s.role}</p>
                      </div>
                      <div className="text-right">
                        {s.is_clocked_in ? (
                          <>
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                              Clocked In
                            </span>
                            <p className="text-xs text-gray-500 mt-1">Since {formatTime(s.clock_in)}</p>
                          </>
                        ) : s.clock_out ? (
                          <>
                            <span className="text-gray-600 text-sm">Clocked Out</span>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatTime(s.clock_in)} - {formatTime(s.clock_out)}
                            </p>
                            <p className="text-xs text-indigo-600">{s.hours_worked}h worked</p>
                          </>
                        ) : (
                          <span className="text-gray-500 text-sm">Not clocked in</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSClockPage;
